import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { KeyValue } from '@angular/common';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { PROGRAMMING_LANGUAGES, ProgrammingLanguage } from '../../utils/programming-languages';
import { PACKAGE_COLUMNS } from '../../utils/package-columns.config';
import { NPMPackage } from 'libs/project/src/lib/utils/models/npm-package-model';
import { PASCAL_CASE_REGEX, addProjectPackageButtonConfig, projectPackageInfoAlertConfig } from '@rappider/shared/definitions';
import { BreadcrumbOption, CodeMirrorMode, FormLayout, HeadingComponentConfig, IconComponentConfig, IconType } from '@rappider/rappider-components/utils';
import { CustomFunctionCreateEditFormItem } from '../../utils/custom-function-crate-edit-form-item.enum';
import { NpmPackageSearchService } from 'libs/project/src/lib/utils/services/npm-package-search.service.ts/npm-package-search.service';
import { ProjectPackageInterface } from '@rappider/api-sdk';
import { NotificationService } from '@rappider/services';
import { isJsonValid } from '@rappider/shared/validators';
import { sortingByNumber } from '../../utils/sorting-by-number';
import { ENVIRONMENT_VARIABLES_COLUMNS } from '../../utils/environment-variables-columns.config';
import { ENVIRONMENT_VARIABLES } from '../../utils/environment-variables.config';
import { ACTIVE_ENVIRONMENT_VARIABLE } from '../../utils/active-environment-variable.config';
import { EnvironmentVariable } from '@rappider/rappider-sdk';
import { MONACO_EDITOR_JAVASCRIPT_CONFIG, MONACO_EDITOR_JSON_CONFIG } from 'libs/shared/src/lib/configs/monaco-editor-language-and-mode-config';
import { MonacoEditorTheme } from 'libs/shared/src/lib/configs/monaco-editor-theme';
import { ThemeMode } from 'libs/shared/src/lib/models/theme';
import { HttpClient } from '@angular/common/http';
import { updateCodeExampleOnLanguage } from '../utils/custom-function-language-monaco-config';

@Component({
  selector: 'rappider-custom-function-create',
  templateUrl: './custom-function-create.component.html',
  styleUrls: ['./custom-function-create.component.scss']
})
export class CustomFunctionCreateComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() mainTitleConfig: HeadingComponentConfig;
  /* flag to display or hide the toolbar */
  @Input() displayToolbar = false;
  @Input() titleConfig: BreadcrumbOption[];

  @Output() createFormSubmit = new EventEmitter<any>();

  createCustomFunctionForm: FormGroup;
  formLayout: FormLayout = FormLayout.Horizontal;
  programmingLanguageOptions = PROGRAMMING_LANGUAGES;
  packageColumns = PACKAGE_COLUMNS;
  environmentVariablesColumns = ENVIRONMENT_VARIABLES_COLUMNS;
  isModalOpen = false;
  selectedPackage: NPMPackage;
  npmPackages: KeyValue<string, NPMPackage>[] = [];
  searchChange$ = new Subject<string>();
  projectPackages: ProjectPackageInterface[];
  packageDetails: KeyValue<string, any>[];
  isLoading = false;
  selectedVersion = null;
  isCustomFunctionLoading = false;

  projectPackageInfoAlertConfig = projectPackageInfoAlertConfig;
  addProjectPackageButtonConfig = addProjectPackageButtonConfig;

  subscriptions: Subscription[];

  codeMirrorSettings = {
    mode: CodeMirrorMode.Javascript,
    lineNumbers: true,
    autoCloseBrackets: true
  };

  loadingIcon: IconComponentConfig = {
    name: 'loading',
    type: IconType.NgZorro
  };

  versions = {
    placement: 'bottomRight',
    items: [],
    labelMode: 'static-label',
  };

  options = [
    {
      label: '',
      value: ''
    }
  ];

  MONACO_EDITOR_JAVASCRIPT_CONFIG = MONACO_EDITOR_JAVASCRIPT_CONFIG;
  MONACO_EDITOR_JSON_CONFIG = MONACO_EDITOR_JSON_CONFIG;

  environments = ENVIRONMENT_VARIABLES;
  activeEnvironmentVariable = ACTIVE_ENVIRONMENT_VARIABLE;
  displayedEnvironmentVariables: EnvironmentVariable[];
  grouppedEnvironmentVariables = {};
  customFunctionCreateEditFormItem = CustomFunctionCreateEditFormItem;
  codeEditorVisible = false;
  requestJSONSchemaVisible = false;
  responseJSONSchemaVisible = false;
  editorConfig = MONACO_EDITOR_JAVASCRIPT_CONFIG;

  constructor(
    private store: Store<any>,
    private npmPackageSearchService: NpmPackageSearchService,
    private formBuilder: FormBuilder,
    private notificationService: NotificationService,
    private httpClient: HttpClient,
    private elRef: ElementRef
  ) { }

  ngOnInit(): void {
    this.subscribeToData();
    this.createCustomFunctionForm = this.formBuilder.group({
      serviceName: [null, [Validators.required, Validators.pattern(PASCAL_CASE_REGEX)]],
      functionName: [null, [Validators.required]],
      description: [null, []],
      programmingLanguage: [ProgrammingLanguage.NodeJs_16, []],
      packages: [null, []],
      DEV: [[], []],
      QA: [[], []],
      PROD: [[], []],
      environmentVariables: [null, []],
      code: [null, [Validators.required]],
      requestJSONSample: [null, [isJsonValid('requestJSONSample', true)]],
      responseJSONSample: [null, [isJsonValid('responseJSONSample', true)]],
    });
    this.getNpmPackages();
    this.changeActiveCodeEditorTab(CustomFunctionCreateEditFormItem.Code);
    this.onProgrammingLanguageChange(ProgrammingLanguage.NodeJs_16);
  }

  ngAfterViewInit(): void {
    this.applyAutofocusToFirstInput();
    this.loadSampleCode();
  }

  loadSampleCode() {
    // if the code is empty load default code example for NodeJs 20
    if (!this.createCustomFunctionForm.get('code').value || this.createCustomFunctionForm.get('code').value === '...') {
      this.loadCodeExampleForProgrammingLanguage(ProgrammingLanguage.NodeJs_20);
    }
  }

  applyAutofocusToFirstInput() {
    const formInputs = this.elRef.nativeElement.querySelectorAll('input');
    if (formInputs.length > 0) {
      const firstInput = formInputs[0];
      firstInput.focus();
    }
  }

  onProgrammingLanguageChange(programmingLanguage: string) {
    this.loadCodeExampleForProgrammingLanguage(programmingLanguage);
  }

  loadCodeExampleForProgrammingLanguage(programmingLanguage: string) {
    // remove colons, dots or dashes from programming language to match file naming convention
    const language = programmingLanguage.replace(/[:.-]/g, '');
    const url = `/assets/code/custom-function-definition/code-examples/${language}.txt`;
    this.httpClient.get(url, { responseType: 'text' }).subscribe((data) => {
      this.createCustomFunctionForm.get('code').setValue(data);
    });
    const editorConfig = updateCodeExampleOnLanguage(language);
    this.editorConfig = {
      ...this.editorConfig,
      language: editorConfig.language
    };
  }


  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  subscribeToData() {
    this.subscriptions = [
      this.subscribeToProjectPackages(),
      this.subscribeToCustomFunctionLoading(),
      this.subscribeToPreferredThemeAndUpdateMonacoTheme()
    ];
  }

  subscribeToProjectPackages() {
    return this.store.select(state => state?.projectPackage?.data).subscribe((projectPackages: ProjectPackageInterface[]) => {
      if (projectPackages) {
        this.projectPackages = projectPackages;
      }
    });
  }

  subscribeToCustomFunctionLoading() {
    return this.store.select(state => state.customFunction?.isLoading).subscribe((isLoading: boolean) => {
      this.isCustomFunctionLoading = isLoading;
    });
  }

  subscribeToPreferredThemeAndUpdateMonacoTheme() {
    return this.store.select(state => state.auth?.activePerson?.preferredTheme).subscribe(preferredTheme => {
      if (preferredTheme === ThemeMode.Light) {
        this.editorConfig.theme = MonacoEditorTheme.Light;
        this.MONACO_EDITOR_JSON_CONFIG.theme = MonacoEditorTheme.Light;
      } else {
        this.editorConfig.theme = MonacoEditorTheme.Dark;
        this.MONACO_EDITOR_JSON_CONFIG.theme = MonacoEditorTheme.Dark;
      }
      this.editorConfig = { ...this.editorConfig };
      this.MONACO_EDITOR_JSON_CONFIG = { ...this.MONACO_EDITOR_JSON_CONFIG };
    });
  }

  setPackageDetailData(selectedPackage: NPMPackage, npmPackageVersions: any) {
    if (selectedPackage && npmPackageVersions) {
      const values = Object.values(npmPackageVersions.versions);

      values.forEach((item, index) => {
        this.options[index] = { label: item['version'], value: item['version'] };
        this.versions.items.push({ label: item['version'] });
      });
      this.packageDetails = [
        {
          key: 'SHARED.NAME',
          value: [selectedPackage.name]
        },
        {
          key: 'SHARED.VERSION',
          value: [this.options.sort((a, b) => sortingByNumber(a.value, b.value))]
        },
        {
          key: 'SHARED.DESCRIPTION',
          value: [selectedPackage.description]
        },
        {
          key: 'SHARED.AUTHOR',
          value: Object.values(selectedPackage.author ?? {})
        },
        {
          key: 'SHARED.PUBLISHER',
          value: Object.values(selectedPackage.publisher ?? {})
        },
        {
          key: 'SHARED.LINKS',
          value: Object.values(selectedPackage.links ?? {})
        },
        {
          key: 'SHARED.KEYWORDS',
          value: selectedPackage.keywords
        }
      ];

      this.selectedVersion = values[0]['version'];

    } else {
      this.packageDetails = null;
    }
  }

  onCreateCustomFunction() {
    if (this.createCustomFunctionForm.valid) {
      const updatedFormBeforeSubmit = [
        ...(this.createCustomFunctionForm.get('DEV')?.value?.map(devValue => ({
          environmentKey: 'dev',
          key: devValue.key,
          value: devValue.value
        })) || []),
        ...(this.createCustomFunctionForm.get('QA')?.value?.map(qaValue => ({
          environmentKey: 'qa',
          key: qaValue.key,
          value: qaValue.value
        })) || []),
        ...(this.createCustomFunctionForm.get('PROD')?.value?.map(prodValue => ({
          environmentKey: 'prod',
          key: prodValue.key,
          value: prodValue.value
        })) || []),
      ];
      this.createCustomFunctionForm.get('environmentVariables').setValue(updatedFormBeforeSubmit);
      const createForm = this.createCustomFunctionForm.value;
      delete createForm.DEV;
      delete createForm.PROD;
      delete createForm.QA;
      this.createFormSubmit.emit(createForm);
    }
  }

  getNameFieldErrorsByErrorKey(errorKey: string) {
    const control = this.createCustomFunctionForm.get(CustomFunctionCreateEditFormItem.ServiceName);
    const isDirty = control?.dirty;
    const errors = control?.errors;
    return isDirty && errors && control.hasError(errorKey);
  }

  openModal() {
    this.isModalOpen = true;
  }

  onModalOk() {
    this.isModalOpen = false;

    if (this.createCustomFunctionForm.value.packages) {
      const isProjectPackageNameExist = this.createCustomFunctionForm.value.packages.some(projectPackage => projectPackage.packageName === this.selectedPackage.name);
      if (!isProjectPackageNameExist) {
        this.createCustomFunctionForm.patchValue({
          packages: [
            ...(this.createCustomFunctionForm?.value?.packages ? <any[]>this.createCustomFunctionForm?.value?.packages : []),
            { 'packageName': this.selectedPackage.name, 'versionNumber': this.selectedVersion }
          ]
        });
      } else {
        this.notificationService.createNotification(
          'error',
          'Error',
          'This package already exists in this custom function.'
        );
      }
    } else {
      this.createCustomFunctionForm.patchValue({
        packages: [
          ...(this.createCustomFunctionForm?.value?.packages ? <any[]>this.createCustomFunctionForm?.value?.packages : []),
          { 'packageName': this.selectedPackage.name, 'versionNumber': this.selectedVersion }
        ]
      });
    }

    this.selectedPackage = null;
    this.packageDetails = null;
    this.selectedVersion = null;
  }

  selectVersionAlert() {
    this.notificationService.createNotification(
      'warning',
      'Warning',
      'Please select a package version!'
    );
  }

  onModalCancel() {
    this.isModalOpen = false;
    this.selectedPackage = null;
    this.packageDetails = null;
    this.selectedVersion = null;
  }

  onSearchValueChange(searchValue: string) {
    if (searchValue === '') {
      this.isLoading = false;
    } else {
      this.isLoading = true;
    }
    this.searchChange$.next(searchValue);
  }

  async onSelectPackage(selectedPackage: NPMPackage) {
    if (selectedPackage) {
      const npmPackageVersions = await this.npmPackageSearchService.getNpmPackageVersions(selectedPackage?.name);
      const sortedNpmPackageVersions = {
        ...npmPackageVersions,
        versions: this.npmPackageSearchService.sortPackageVersions(npmPackageVersions)
      };
      this.setPackageDetailData(selectedPackage, sortedNpmPackageVersions);
    } else {
      this.setPackageDetailData(null, {});
    }
  }

  getNpmPackages() {
    const npmPackages$: Observable<any> = this.searchChange$
      .asObservable().pipe(
        debounceTime(1500),
        switchMap((searchValue) => {
          if (searchValue) {
            return this.npmPackageSearchService.getNpmPackages(searchValue);
          } else {
            return of(null);
          }
        })
      );

    this.setNpmPackageSelectOptions(npmPackages$);
  }

  setNpmPackageSelectOptions(packages: Observable<any>) {
    packages.subscribe(packages => {
      this.npmPackages = packages?.results?.map(result => ({
        key: result.package.name,
        value: result.package
      })) ?? [];
      this.isLoading = false;
    });
  }

  onVersionChange(value: any) {
    this.selectedVersion = value;
  }

  selectEnvironment(activeEnvironmentVariable) {
    this.activeEnvironmentVariable = activeEnvironmentVariable;
  }

  onCreateFormSubmit(formData) {
    this.environments.forEach(environment => {
      if (environment === formData.activeEnvironmentVariable) {
        this.createCustomFunctionForm.get(environment).setValue(
          [...this.createCustomFunctionForm.get(environment).value, { key: formData.key, type: formData.type, value: formData.value }]
        );
      } else {
        this.createCustomFunctionForm.get(environment).setValue(
          [...this.createCustomFunctionForm.get(environment).value, { key: formData.key, type: null, value: null }]
        );
      }
    });
    this.grouppedEnvironmentVariables = {
      DEV: this.createCustomFunctionForm.get('DEV').value,
      QA: this.createCustomFunctionForm.get('QA').value,
      PROD: this.createCustomFunctionForm.get('PROD').value
    };
    this.onHandleActiveEnvironmentVariableTabList(this.activeEnvironmentVariable);
  }

  onEditFormSubmit(formData) {
    const formValue = this.createCustomFunctionForm.get(this.activeEnvironmentVariable).value;
    const updatedDevValue = formValue.map(item => {
      if (item.key === formData.editableModalData.key) {
        item.key = formData.key;
        item.type = formData.type;
        item.value = formData.value;
      }
      return item;
    });
    this.createCustomFunctionForm.get(this.activeEnvironmentVariable).setValue(updatedDevValue);
    this.grouppedEnvironmentVariables = {
      ...this.grouppedEnvironmentVariables,
      [this.activeEnvironmentVariable]: this.createCustomFunctionForm.get(this.activeEnvironmentVariable).value
    };
    this.onHandleActiveEnvironmentVariableTabList(this.activeEnvironmentVariable);
  }

  onDeleteFormItem(formData) {
    const formValue = this.createCustomFunctionForm.get(this.activeEnvironmentVariable).value;
    const updatedFromValue = formValue.filter(item => item.key !== formData.key);
    this.createCustomFunctionForm.get(this.activeEnvironmentVariable).setValue(updatedFromValue);
    this.grouppedEnvironmentVariables = {
      ...this.grouppedEnvironmentVariables,
      [this.activeEnvironmentVariable]: this.createCustomFunctionForm.get(this.activeEnvironmentVariable).value
    };
    this.onHandleActiveEnvironmentVariableTabList(this.activeEnvironmentVariable);
  }

  onHandleActiveEnvironmentVariableTabList(activeEnvironment) {
    this.activeEnvironmentVariable = activeEnvironment;
    this.displayedEnvironmentVariables = this.grouppedEnvironmentVariables[activeEnvironment];
  }

  changeActiveCodeEditorTab(activeTab) {
    if (activeTab === CustomFunctionCreateEditFormItem.Code) {
      this.codeEditorVisible = true;
      this.requestJSONSchemaVisible = false;
      this.responseJSONSchemaVisible = false;
    } else if (activeTab === CustomFunctionCreateEditFormItem.RequestJSONSchema) {
      this.codeEditorVisible = false;
      this.requestJSONSchemaVisible = true;
      this.responseJSONSchemaVisible = false;
    } else if (activeTab === CustomFunctionCreateEditFormItem.ResponseJSONSchema) {
      this.codeEditorVisible = false;
      this.requestJSONSchemaVisible = false;
      this.responseJSONSchemaVisible = true;
    }
  }

}
